import { Core } from "@faire/design-tokens";
import { ResetPassword } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ResetPassword";
import { SignInController } from "@faire/retailer-visitor-shared/components/AppSignIn/components/SignInController";
import { useRedirectAfterSignIn } from "@faire/retailer-visitor-shared/components/AppSignIn/useRedirectAfterSignIn";
import { FaireHelmet } from "@faire/retailer-visitor-shared/components/Helmet/index";
import { useSSOErrorStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import {
  MANAGE_EMAIL_PREFERENCES,
  OVERWRITE_COPY_KEY,
} from "@faire/retailer-visitor-shared/views/settings/email-preferences/EmailPreferencesPage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { IQueryParamsProps } from "@faire/web--source/common/query-params/IQueryParamsProps";
import { withQueryParams } from "@faire/web--source/common/query-params/withQueryParams";
import { pathMatchesRoute } from "@faire/web--source/common/routes/RouteMatcher";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import { Modal } from "@faire/web--source/slate/Modal";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { route as videoPageRoute } from "@faire/web-api--source/routes/www/video/videoToken";
import qs from "query-string";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useHistory, withRouter } from "react-router-dom";
import styled from "styled-components";

interface SignInParams {
  overwriteCopy?: string;
  quickLogin?: string;
  resetEmail?: string;
  signIn?: string;
  email?: string;
  ifrp?: string;
  livestream?: string;
  redirectTo?: string;
  error?: string;
}

const AppSignInBase: FC<IQueryParamsProps<SignInParams>> = ({
  queryParams,
}) => {
  const [resetSuccess, setResetSuccess] = useState(false);

  const {
    overwriteCopy,
    quickLogin,
    resetEmail,
    signIn,
    email,
    ifrp,
    livestream,
    redirectTo,
  } = queryParams;

  const { resetErrorMessage } = useSSOErrorStore();

  const location = useLocation();

  const { user } = useUserStore(["user"]);

  const { replace } = useHistory();

  const redirectAfterSignIn = useRedirectAfterSignIn();

  const handleOnClose = useCallback(() => {
    const newQuery = qs.stringify({
      ...queryParams,
      [OVERWRITE_COPY_KEY]: undefined,
      quickLogin: undefined,
      resetEmail: undefined,
      signIn: undefined,
      signUp: undefined,
      email: undefined,
      ifrp: undefined,
      livestream: undefined,
      redirectTo: undefined,
      error: undefined,
    });
    const queryString = newQuery ? `?${newQuery}` : "";
    resetErrorMessage();
    replace(`${location.pathname}${queryString}`);
  }, [queryParams, resetErrorMessage, replace, location.pathname]);

  const handleOnLogin = useCallback(
    (user: IUser) => redirectAfterSignIn(location, user),
    [location, redirectAfterSignIn]
  );

  const handleOnReset = () =>
    user ? handleOnLogin(user) : setResetSuccess(true);

  useEffect(() => {
    if (!!quickLogin && !!user) {
      if (user?.is_employee) {
        handleOnLogin(user);
      } else {
        handleOnClose();
      }
    }
  }, [quickLogin, user, handleOnClose, handleOnLogin]);

  const dismissible =
    !livestream && !pathMatchesRoute(location, videoPageRoute);

  const isBrandOnboardingEmailVerification =
    redirectTo?.includes("email_verification_token") &&
    redirectTo?.includes("validation_token") &&
    redirectTo?.includes("brand-onboarding");

  const specialSignInTitle = useSpecialSignInTitle({
    resetSuccess,
    isBrandOnboardingEmailVerification,
    overwriteCopy,
  });

  if (!!quickLogin && !!user) {
    return null;
  }

  return (
    <Modal
      onClose={handleOnClose}
      showCloseButton={dismissible}
      shouldCloseOnOverlayClick={dismissible}
      width="auto"
      height="auto"
      padding="0"
      noPortal
      data-test-id="signInModal"
    >
      <ResponsiveContainer>
        {location.pathname !== "/" ? <FaireHelmet noindex /> : null}
        <MobileOnlyHeader>
          <FaireLogo size={110} />
        </MobileOnlyHeader>
        {signIn === "reset-password" && !resetSuccess ? (
          <ResetPassword onReset={handleOnReset} resetToken={ifrp} />
        ) : (
          <SignInController
            onLogin={handleOnLogin}
            email={email}
            sendResetEmail={!!resetEmail}
            signInTitle={specialSignInTitle}
            isResetPasswordSuccess={resetSuccess}
          />
        )}
      </ResponsiveContainer>
    </Modal>
  );
};

export const AppSignIn = withRouter(withQueryParams(AppSignInBase));

const useSpecialSignInTitle = ({
  resetSuccess,
  isBrandOnboardingEmailVerification,
  overwriteCopy,
}: {
  resetSuccess: boolean;
  isBrandOnboardingEmailVerification?: boolean;
  overwriteCopy?: string;
}): string | undefined => {
  const { strictLocalize } = useStrictLocalization();
  if (
    resetSuccess ||
    isBrandOnboardingEmailVerification ||
    overwriteCopy === MANAGE_EMAIL_PREFERENCES
  ) {
    return strictLocalize({
      defaultMessage: "Sign in",
      description: {
        text: "Title shown to users sigining",
      },
    });
  }
  return undefined;
};

const ResponsiveContainer = styled.div`
  width: 100%;
  height: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    display: flex;
    flex-direction: column;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    display: flex;
    flex-direction: row;
  }
`;

const MobileOnlyHeader = styled.div`
  display: flex;
  align-items: center;
  height: 54px;
  width: 100%;
  border-bottom: 1px solid ${Core.border.muted};
  justify-content: flex-start;
  padding: ${getSpacing(0, "3x")};
  ${responsiveMediaQueries.tabletAndAbove} {
    display: none;
  }
`;
